<template>
    <div>
        <section class="employees account-section" v-if="loaded">
            <div class="account-container">

                <div class="upgrade-header d-flex flex-column flex-lg-row mb-5 mb-4 align-items-lg-center justify-content-lg-between" v-if="organization && organization.subscription_status == 'canceled'">
                    <div>
                        <h3 class="m-0 mb-1">Your account was canceled</h3>
                        <div class="color-light-bluegray text-small m-0">Please resubscribe if you would like to activate your account again</div>
                    </div>
                    <div class="ml-lg-3 mt-4 mt-lg-0">
                        <button type="button" class="btn btn-primary" v-on:click="startThePurchase()">Resubscribe</button>
                    </div>
                </div><!-- .upgrade-header -->
                <div class="upgrade-header d-flex flex-column flex-lg-row mb-5 mb-4 align-items-lg-center justify-content-lg-between" v-else-if="organization && organization.subscription_status == 'graceperiod'">
                    <div>
                        <h3 class="m-0 mb-1">Your account was canceled</h3>
                        <div class="color-light-bluegray text-small m-0">You may continue to use your account until your subscription time period is over.</div>
                    </div>
                    <div class="ml-lg-3 mt-4 mt-lg-0">
                        <button type="button" class="btn btn-primary" v-on:click="showResumeModal = true">Resume</button>
                    </div>
                </div><!-- .upgrade-header -->
                <div class="upgrade-header d-flex flex-column flex-lg-row mb-5 mb-4 align-items-lg-center justify-content-lg-between" v-else-if="organization && organization.subscription_status == 'past_due'">
                    <div>
                        <h3 class="m-0 mb-1">Your account has a payment issue!</h3>
                        <div class="color-light-bluegray text-small m-0">Please update your credit card in the billing section so that the account isn't canceled in the near future.</div>
                    </div>
                    <div class="ml-lg-3 mt-4 mt-lg-0">
                        <a href="/billing" class="btn btn-primary">Update&nbsp;Card</a>
                    </div>
                </div><!-- .upgrade-header -->

                <div class="employee-list" v-bind:class="{ 'inactive' : organization && (organization.subscription_status == 'canceled' || organization.subscription_status == 'past_due') }">
                    <div class="account-cancelled-whiteout" v-if="organization && (organization.subscription_status == 'canceled' || organization.subscription_status == 'past_due')"></div>
                    <div class="mb-5 d-flex flex-column flex-lg-row align-items-lg-center justify-content-lg-between">
                        <div>
                            <h1 class="m-0 h2">Employees</h1>
                            <div class="mt-1 color-light-bluegray text-xsmall" v-if="loaded">{{ organization.employees_count }} total employees</div>
                            <div class="mt-1 color-light-bluegray text-xsmall" v-else>Loading employees...</div>
                        </div>
                        <div class="ml-lg-3 mt-3 mt-lg-0 d-flex align-items-center">
                            <form class="form-inline mt-2 mt-md-0" v-if="search_box_displayed">
                                <input class="form-control search-input" type="search" placeholder="Search Employees" aria-label="Search" v-model="searchtext">
                                <button class="btn btn-mediumblue btn-search btn-search-open" type="submit" v-on:click.prevent="getResults()"><span class="sr-only">Search</span></button>
                            </form>
                            <button class="btn btn-mediumblue btn-search" v-if="!search_box_displayed" v-on:click.prevent="search_box_displayed = true"><span class="sr-only">Search</span></button>
                            <button class="transparent-bg-close-button" v-if="group_box_displayed" v-on:click.prevent="group_box_displayed = false"><span class="sr-only">Close</span></button>
                            <div class="position-relative ml-2">
                                <button class="btn btn-mediumblue btn-group" v-on:click.prevent="group_box_displayed = !group_box_displayed"><span class="sr-only">Group</span></button>
                                <div class="group-area" v-if="group_box_displayed">
                                    <div class="group-title group-padding">Filter By Group</div>
                                    <ul class="group-list">
                                        <li v-for="group in groups" v-bind:key="group.id"><a href="#" v-text="group.name" v-bind:class="{ selected: group.selected }" v-on:click.prevent="selectGroup(group)"></a></li>
                                        <li class="no-groups" v-if="groups.length === 0">No Groups – Create &amp; Assign Groups on the Signatures Screen.</li>
                                    </ul>
                                </div>
                            </div>
                            <a v-bind:href="getExportUrl()" class="ml-2 btn btn-primary">Export</a>
                            <a class="btn btn-primary ml-2 d-none d-sm-inline-block" href="/signatures">Manage Signatures</a>
                        </div>
                    </div>
                    <div class="mb-3 mt-n3 alert alert-info alert-dismissible fade show text-small" role="alert" v-if="user.before_employee_sigs && user.dismiss_employee_sigs_notice === false">
                        We recently added an exciting new feature for Signature.email that <strong>saves whole employee signatures!</strong> This makes it easier for you and your employees to recall signatures and edit them. Unfortunately, your existing employee signatures in our system will be empty (except email address) until the employee signature is updated. <a href="https://signature.email/blog/save-employee-signatures-functionality" target="_blank" class="alert-link">Learn More</a>
                        <button type="button" class="close" data-dismiss="alert" aria-label="Close" v-on:click.prevent="dismissEmployeeSigsNotice()">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="mb-3 mt-n3 d-flex align-items-center justify-content-between color-light-bluegray text-xsmall inset-area px-3 py-2" v-if="searchtext_applied || selectedGroup">
                        <div>
                            <strong v-if="employees">{{ employees.total }}</strong> employees 
                            <template v-if="selectedGroup">
                                in group <strong>{{ selectedGroup.name }}</strong>
                            </template>
                            <template v-if="searchtext_applied">
                                with text <strong>{{ searchtext_applied }}</strong>
                            </template> 
                        </div>
                        <div>
                            <button class="btn btn-sm btn-mediumblue" v-on:click.prevent="clearFilters()">Clear</button>
                        </div>
                    </div>
                    <div class="card card-padding-table">
                        <table class="table employee-table overflow-hidden m-0" v-if="employeesLoaded && employees">
                            <tr>
                                <th class="border-top-0 text-uppercase text-2xsmall color-light-bluegray pb-1 pt-2 pl-0">Email</th>
                                <th class="border-top-0 text-uppercase text-2xsmall color-light-bluegray pb-1 pt-2" v-if="!organization.old_mode_no_employee_sigs">Name</th>
                                <th class="border-top-0 text-uppercase text-2xsmall color-light-bluegray pb-1 pt-2" v-if="!organization.old_mode_no_employee_sigs">Title</th>
                                <th class="border-top-0 text-uppercase text-2xsmall color-light-bluegray pb-1 pt-2">Created</th>
                                <th class="border-top-0 text-uppercase text-2xsmall color-light-bluegray pb-1 pt-2">Sigs</th>
                                <th class="border-top-0 text-uppercase text-2xsmall color-light-bluegray pb-1 pt-2 pr-0 text-right">Actions</th>
                            </tr>
                            <tr v-for="employee in employees.data" v-bind:key="employee.id">
                                <td class="text-xsmall align-middle pl-0">
                                    <a v-bind:href="'mailto:' + employee.email" class="email-link">{{ employee.email }}</a>
                                </td>
                                <td class="text-xsmall align-middle" v-text="employee.full_name" v-if="!organization.old_mode_no_employee_sigs"></td>
                                <td class="text-xsmall align-middle" v-text="employee.title" v-if="!organization.old_mode_no_employee_sigs"></td>
                                <td class="text-xsmall align-middle" v-text="employee.created_at_formatted"></td>
                                <td class="text-xsmall align-middle" v-text="employee.employee_signatures_count"></td>
                                <td class="text-right align-middle pr-0">
                                    <a v-bind:href="'/employees/' + employee.id" class="btn btn-mediumblue btn-sm">View</a>
                                    <a href="#" class="btn btn-mediumblue btn-sm" v-on:click.prevent="deleteEmployee(employee)">Delete</a>
                                </td>
                            </tr>
                            <tr v-if="employeesLoaded && employees && employees.data.length === 0">
                                <td class="text-xsmall align-middle pl-0" colspan="6">
                                    <template v-if="searchtext_applied || selectedGroup">
                                        No employees found.
                                    </template>
                                    <template v-else>
                                        No employees created yet.
                                    </template>
                                </td>
                            </tr>
                        </table><!-- .table -->
                        <div v-if="employeesLoaded === false" class="text-xsmall color-light-bluegray pl-0 pb-1">
                            <div class="d-flex align-items-center">
                                <div class="inline-loading"></div><div class="d-inline-block">Loading...</div>
                            </div>
                        </div>
                    </div><!-- .employee-card -->
                    <div class="mt-4 table-pagination" v-if="employeesLoaded">
                        <pagination :data="employees" :limit="6" @pagination-change-page="getResults" />
                    </div>
                    <div class="mt-4 d-flex flex-column flex-lg-row align-items-lg-center">
                        <div>
                            <button class="add-new btn btn-primary" v-on:click.prevent="openTemplatesModal()">New Employee Signature</button>
                        </div>
                        <div class="color-light-bluegray text-xsmall mt-3 mt-lg-0 ml-lg-3" v-if="loaded && organization">{{ employeesAvailable }}</div>
                    </div>
                </div>                

                <template v-if="load_sale_active == 'yes'">
                    <div class="mb-3 pl-5 pr-4" v-if="loaded && sale_active && (organization.plan_name !== 'plan1' && organization.plan_name !== 'plan2')">
                        <div class="sale-banner"><h5>Black Friday &amp; Cyber Monday Sale This Weekend Only!</h5> <div>Save <strong>$5</strong> on Individual signatures and <strong>50%</strong> off Yearly Plans!</div></div>
                    </div>
                </template>

                <template v-if="organization && !organization.plan_name">
                    <div class="upgrade-header d-flex flex-column flex-lg-row mt-5 mb-4 align-items-lg-center justify-content-lg-between">
                        <div>
                            <h3 class="m-0 mb-1">You are currently on the free plan</h3>
                            <div class="color-light-bluegray text-small m-0">Upgrade today to unlock the full power of signature.email:</div>
                        </div>
                        <div class="ml-lg-3 mt-4 mt-lg-0">
                            <button type="button" class="btn btn-primary" v-on:click="startThePurchase()">Purchase&nbsp;Plan</button>
                        </div>
                    </div><!-- .upgrade-header -->
                    
                    <div class="upgrade-cards inset-cards mt-5">
                        <div class="card mb-2 mb-md-4 w-auto">
                            <div class="card-body">
                                <div class="d-md-flex align-items-md-center justify-content-md-start">
                                    <div class="image mx-md-4"><img v-bind:src="load_aws_url + '/website/icon-cloud-save.svg'"></div>
                                    <div class="text-center text-md-left">
                                        <h4 class="mb-2 mb-md-1">Save Your Signature</h4>
                                        <div>Free signatures expire after 30 days. Upgrade to save your signature on this site and edit at any time.</div>
                                    </div>
                                </div>
                            </div><!-- .card-body -->
                        </div><!-- .card -->
                        <div class="d-flex flex-column flex-md-row align-items-stretch">
                            <div class="card">
                                <div class="card-body">
                                    <div class="image"><img v-bind:src="load_aws_url + '/website/icon-templates.svg'"></div>
                                    <h4 class="mb-2">Access Premium Templates</h4>
                                    <div>Our beautifully designed templates help you look more professional and stand out in your industry.</div>
                                </div><!-- .card-body -->
                            </div><!-- .card -->
                            <div class="card">
                                <div class="card-body">
                                    <div class="image"><img v-bind:src="load_aws_url + '/website/icon-editor.svg'"></div>
                                    <h4 class="mb-2">Additional Editor Capabilities</h4>
                                    <div>Premium fields and advanced editing capabilities allow you to create beautiful designs with unique features.</div>
                                </div><!-- .card-body -->
                            </div><!-- .card -->
                            <div class="card">
                                <div class="card-body">
                                    <div class="image"><img v-bind:src="load_aws_url + '/website/icon-share.svg'"></div>
                                    <h4 class="mb-2">Generator <br>Links</h4>
                                    <div>The easiest way to maintain email signature consistency for your entire team.</div>
                                </div><!-- .card-body -->
                            </div><!-- .card -->
                        </div><!-- .d-flex -->
                    </div><!-- .upgrade-cards -->
                </template>
                <template v-else-if="organization && organization.purchased && organization.plan_name == 'solo'">
                    <div class="upgrade-header inset-area d-flex flex-column flex-lg-row mt-5 mb-4 align-items-lg-center justify-content-lg-between">
                        <div>
                            <h4 class="m-0 mb-1 color-light-bluegray">You are currently on the <strong>Individual</strong> plan</h4>
                            <div class="color-light-bluegray text-small m-0">Upgrade today to create more signatures and share them with your team using generator links.</div>
                        </div>
                        <div class="ml-lg-3 mt-4 mt-lg-0">
                            <button type="button" class="btn btn-primary" v-on:click="startPurchaseIndividual()">Individual&nbsp;Plan</button>
                            <button type="button" class="btn btn-mediumblue ml-3" v-on:click="startTheUpgrade()">Company&nbsp;Plan</button>
                        </div>
                    </div><!-- .upgrade-header -->
                </template>
                <template v-else-if="organization && organization.purchased && organization.subscription_status == 'active' && organization.plan_name !== 'solo'">
                    <div class="upgrade-header inset-area d-flex flex-column flex-lg-row mt-5 mb-4 align-items-lg-center justify-content-lg-between">
                        <div>
                            <h4 class="m-0 mb-1 color-light-bluegray">You are currently on the <strong>{{ nameofPlan }}</strong> plan</h4>
                            <div class="color-light-bluegray text-small m-0" v-if="organization.plan_name == 'trial'">Contact us if you have additional needs or questions</div>
                            <div class="color-light-bluegray text-small m-0" v-else-if="organization.plan_name == 'free'">Complimentary plan includes {{ organization.allowed_employees}} employees</div>
                            <div class="color-light-bluegray text-small m-0" v-else-if="organization.plan_name === 'newplan6' || organization.plan_name === 'enterprise'">Contact us if you have additional needs or questions</div>
                            <div class="color-light-bluegray text-small m-0" v-else>Upgrade today to create more than {{ organization.allowed_employees}} employees</div>
                        </div>
                        <div class="ml-lg-3 mt-4 mt-lg-0">
                            <button type="button" class="btn btn-mediumblue" v-on:click="startThePurchase()" v-if="organization.plan_name == 'trial' || organization.plan_name == 'free'">Purchase&nbsp;Plan</button>
                            <button type="button" class="btn btn-mediumblue" v-on:click="startTheUpgrade()">Modify&nbsp;Plan</button>
                        </div>
                    </div><!-- .upgrade-header -->
                </template>
            </div>
        </section>

        <div id="resume-modal" class="resume-modal modal ui-light-theme" v-bind:class="{ show: showResumeModal }" tabindex="-1" role="dialog" aria-modal="true" v-if="showResumeModal" v-on:click.self.prevent="showResumeModal = false">
            <div class="modal-dialog" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Resume Your Subscription</h5>
                        <button type="button" class="close" v-on:click.prevent="showResumeModal = false" aria-label="Close">
                            <span aria-hidden="true">×</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <p>You would like to resume your subscription and start payments again?</p>
                        <div class="mt-4">
                            <button class="btn btn-lg btn-primary btn-block" v-on:click.prevent="resumeSubscription()">Resume Subscription</button>
                        </div>
                    </div>
                </div>
            </div>
        </div><!-- .resume-modal -->

        <div id="templates-modal" class="templates-modal modal ui-light-theme" v-bind:class="{ show: showTemplatesModal }" tabindex="-1" role="dialog" aria-modal="true" v-if="showTemplatesModal" v-on:click.self.prevent="showTemplatesModal = false">
            <div class="modal-dialog" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Create New Employee Signature</h5>
                        <button type="button" class="close" v-on:click.prevent="showTemplatesModal = false" aria-label="Close">
                            <span aria-hidden="true">×</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <p>Please choose a template for this new employee signature.</p>
                        <a v-bind:href="templateLink(template)" v-for="template in templates" v-bind:key="template.id" class="card signature-card signature-card-select">
                            <div class="card-body">
                                <div class="d-flex align-items-center justify-content-between">
                                    <div class="d-flex align-items-center">
                                        <div class="status">
                                            <div class="status-circle" v-bind:class="statusSlug(template)"></div>
                                            <div class="status-name">
                                                <template v-if="template.single_signature === false && template.status == 'Draft'">
                                                    Team<br>Signature
                                                </template>
                                                <template v-else-if="template.single_signature === false && template.status == 'Published'">
                                                    Team<br>Generator
                                                </template>
                                                <template v-else>
                                                    {{ template.status }}
                                                </template>
                                            </div>
                                        </div>
                                        <div class="title-details">
                                            <h4 class="signature-title">{{ template.name }}</h4>
                                            <div class="signature-meta">{{ template.created_at | toDate }}</div>
                                        </div>
                                    </div>
                                    <div class="pl-3">
                                        <div class="btn btn-primary btn-sm">Create</div>
                                    </div>
                                </div>  
                            </div>
                        </a>
                        <div class="color-light-bluegray card signature-card signature-card-select" v-if="templates.length === 0">
                            <div class="card-body">
                                No team templates created yet, you may need to convert one of your Individual signatures into a team template.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div><!-- .templates-modal -->

        <div id="out-of-employees-modal" class="out-of-employees-modal modal ui-light-theme" v-bind:class="{ show: showOutOfEmployeesModal }" tabindex="-1" role="dialog" aria-modal="true" v-if="showOutOfEmployeesModal" v-on:click.self.prevent="showOutOfEmployeesModal = false">
            <div class="modal-dialog" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Out of Employees</h5>
                        <button type="button" class="close" v-on:click.prevent="showOutOfEmployeesModal = false" aria-label="Close">
                            <span aria-hidden="true">×</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <p>Oh no! You have used up <strong>{{ organization.employees_count }}</strong> of your <strong>{{ organization.allowed_employees }}</strong> employees included in your plan. You won't be able to create more until you delete some that are no longer needed or upgrade to a larger plan that fits your organization.</p>
                    </div>
                </div>
            </div>
        </div>

        <purchase-modal v-bind:show-purchase-modal="showPurchaseModal" v-bind:load_stripe_key="load_stripe_key" v-bind:load_client_secret="load_client_secret" view="all-signatures" v-on:closepurchasemodal="showPurchaseModal = false" theme="light" v-bind:initial-chosen-plan="purchaseModalPlan" v-on:purchasesuccessful="purchaseSuccessful" v-bind:initial-user-id="user.id" v-bind:initial-organization-id="organization.id" v-bind:initial-email="user.email" v-if="loaded"></purchase-modal>

        <upgrade-modal v-if="loaded && user && organization" v-bind:show-upgrade-modal="showUpgradeModal" v-bind:user="user" v-bind:organization="organization" theme="light" v-on:upgradesuccessful="upgradeSuccessful" v-on:closeupgrademodal="showUpgradeModal = false"></upgrade-modal>

    </div>
</template>

<script>
    import { DateTime } from "luxon";
    export default {
        props: {
            load_aws_url: {
                type: String,
                default: null,
            },
            load_stripe_key: {
                type: String,
                default: null,
            },
            load_client_secret: {
                type: String,
                default: null,
            },
            load_sale_active: {
                type: String,
                default: 'no',
            },
            load_env: {
                type: String,
                default: 'production',
            }
        },
        data: function () {
            return {
                monthly: false,
                showPurchaseModal: false,
                purchaseModalPlan: null,
                showUpgradeModal: false,
                showGeneratorInfoModal: false,
                showDupliateInfoModal: false,
                showPublishInfoModal: false,
                showAdditionalSigModal: false,
                showEditNameModal: false,
                showResumeModal: false,
                editSignatureNameSig: null,
                additionalSigs: 1,
                additionalError: null,
                additionalPurchaseSuccessful: false,
                additionalPurchaseLoading: false,
                user: null,
                organization: null,
                employees: null,
                templates: null,
                loaded: false,
                employeesLoaded: false,
                group_box_displayed: false,
                selectedGroup: null,
                groups: null,
                search_box_displayed: false,
                searchtext: '',
                searchtext_applied: '',
                solo_plan_price: 19,
                solo_payment_method: false,
                sale_active: false,
                showTemplatesModal: false,
                showOutOfEmployeesModal: false,
            }
        },
        mounted() {
            var self = this;
            axios.post('/employees/data')
            .then(function(response) {
                self.user = response.data.user;
                self.organization = response.data.organization;
                self.purchaseModalPlan = self.organization.plan_name;
                self.groups = response.data.groups;
                self.templates = response.data.templates;
                self.purchased = response.data.purchased;
                self.subscription_status = response.data.subscription_status;
                self.loaded = true;
                self.getResults();
            }.bind(this)).catch(function(data) {
                console.log('error');
            });
        },
        computed: {
            nameofPlan: function() {
                if (this.organization.plan_name === 'enterprise') {
                    return "Enterprise";
                } else if (this.organization.plan_name === 'free') {
                    return "Complimentary";
                } else if (this.organization.plan_name === 'trial') {
                    return "Trial";
                } else {
                    return "Company";
                }
            },
            additionalPurchaseButtonText: function () {
                if (this.additionalPurchaseLoading) {
                    return "Please wait... <img src='/img/spinner-white.gif' width='30' height='30'>";
                } else {
                    var additionalTotal = this.additionalSigs * this.solo_plan_price;
                    if (this.additionalSigs === 1) {
                        return "Purchase " + this.additionalSigs + " Signature for $" + additionalTotal;
                    } else {
                        return "Purchase " + this.additionalSigs + " Signatures for $" + additionalTotal;
                    }
                }
            },
            employeesAvailable: function() {
                if (this.organization.plan_name && this.organization.plan_name !== '') {
                    if (this.organization.allowed_employees === 99999) {
                        return "Unlimited employees available";
                    } else {
                        var available = this.organization.allowed_employees - this.organization.employees_count;
                        return available + " employees available";
                    }
                } else {
                    return "No employees available";
                }
            }
        },
        filters: {
            toDate: function(date) {
                return DateTime.fromISO(date).toLocaleString();
            },
            toLowerCase: function(string) {
                return string.toLowerCase().trim();
            }
        },
        methods: {
            dismissEmployeeSigsNotice: function() {
                this.user.dismiss_employee_sigs_notice = true;
                var self = this;
                axios.post('/signatures/dismiss-notice', { dismiss_employee_sigs_notice: true } )
                .then(function(response) {
                    
                }.bind(this)).catch(function(data) {
                    console.log('error');
                });
            },
            clearFilters: function() {
                if (this.selectedGroup) {
                    this.selectedGroup.selected = false;
                }
                this.selectedGroup = null;
                this.searchtext = '';
                this.searchtext_applied = '';
                this.getResults();
            },
            getExportUrl: function() {
                var export_url = '/employees/export?';
                if (this.selectedGroup) {
                    export_url = export_url + 'group=' + this.selectedGroup.id;
                }
                if (this.searchtext_applied && this.searchtext_applied !== null) {
                    if (this.selectedGroup) {
                        export_url = export_url + '&';
                    }
                    export_url = export_url + 'search=' + this.searchtext_applied;
                }
                return export_url;
            },
            selectGroup: function(group) {
                var self = this;
                if (self.selectedGroup && self.selectedGroup.id === group.id) {
                    self.selectedGroup = null;
                    group.selected = false;
                } else {
                    self.groups.forEach(function ( onegroup, index ) {
                        onegroup.selected = false;
                    });
                    group.selected = true;
                    self.selectedGroup = group;
                }
                self.group_box_displayed = false;
                self.getResults();
            },
            getResults: function(page = 1) {
                var self = this;
                self.employeesLoaded = false;
                var selectedGroupId = null;
                if (self.selectedGroup) {
                    selectedGroupId = self.selectedGroup.id;
                }
                axios.post('/employees/data/search', { page: page, search: self.searchtext, group: selectedGroupId })
                .then(function(response) {
                    self.employees = response.data.employees;
                    self.searchtext_applied = self.searchtext;
                    self.employeesLoaded = true;
                }.bind(this)).catch(function(data) {
                    console.log('error');
                });
            },
            statusSlug(template) {
                if (template.single_template === true) {
                    return 'published';
                } else {
                    return (template.status).toLowerCase().trim();
                }
            },
            deleteEmployee: function(employee) {
                var confirmation = confirm("Are you sure you want to delete " + employee.full_name + " and all of their signature data permanently? They will no longer be able to recall and edit their signature from the link they were sent via email!");
                if (confirmation == true) {
                    var self = this;
                    axios.post('/employees/' + employee.id + '/delete')
                    .then(function(response) {
                        console.log(response);
                        if (response.data.status == 'success') {
                            var employeeIndex = self.employees.data.findIndex(function(theemployee) {
                                return theemployee.id === employee.id;
                            }, employee);
                            if (employeeIndex !== -1) {
                                self.employees.data.splice(employeeIndex, 1);
                                Vue.notify({
                                    group: 'bottomright',
                                    type: 'success',
                                    title: 'Employee Deleted',
                                });
                            }
                        }
                    }.bind(this)).catch(function(data) {
                        console.log('error');
                    });
                }
            },
            templateLink: function(template) {
                if (template.slug && template.status === 'Published') {
                    if (this.load_env == 'development') {
                        return "http://" + template.slug + ".signatureemail.lndo.site";
                    } else {
                        return "https://" + template.slug + ".signature.email";
                    }
                } else {
                    return "/signatures/view/" + template.id;
                }
            },
            openTemplatesModal: function() {
                if (this.organization.employees_count >= this.organization.allowed_employees) {
                    this.showOutOfEmployeesModal = true;
                } else {
                    if (this.templates.length === 1) {
                        window.location.href = this.templateLink(this.templates[0]);
                    } else {
                        this.showTemplatesModal = true;
                    }
                }
            },
            startPurchaseIndividual() {
                this.purchaseModalPlan = 'solo';
                this.showPurchaseModal = true;
            },
            startThePurchase() {
                this.purchaseModalPlan = 'company';
                this.showPurchaseModal = true;
            },
            startTheUpgrade() {
                this.showUpgradeModal = true;
            },
            purchaseSuccessful: function(user, organization) {
                this.user = user;
                this.organization = organization;
            },
            upgradeSuccessful: function(plan_name, period, templates, allowed_employees, sharing) {
                this.organization.plan_name = plan_name;
                this.organization.period = period;
                this.organization.templates = templates;
                this.organization.allowed_employees = allowed_employees;
                this.organization.sharing = sharing;
            },
            // statusSlug(signature) {
            //     //console.log(signature);
            //     if (signature.single_signature === 1) {
            //         return 'published';
            //     } else {
            //         return (signature.status).toLowerCase().trim();
            //     }
            // },
            // editLink(id) {
            //     return '/generator/edit/' + id;
            // },
            // statsLink(id) {
            //     return '/signature/stats/' + id;
            // },
            
            // deleteSignature(id) {
            //     if (id) {
            //         var confirmation = confirm("Are you sure you want to delete this signature permanently?");
            //         if (confirmation == true) {
            //             var self = this;
            //             axios.delete('/signature/' + id)
            //             .then(function(response) {
            //                 if (response.data.status == 'success') {
            //                     //console.log("deleted");
            //                     self.signatures.forEach(function ( signature, index ) {
            //                         if (signature.id == id) {
            //                             self.signatures.splice(index, 1);
            //                         }
            //                     }, self, id);
            //                 } else if (response.data.status == 'error') {
            //                     alert(reponse.data.message);
            //                 } else {
            //                     alert("Sorry, an unknown issue occured");
            //                 }
            //             }.bind(this)).catch(function(data) {
            //                 console.log('error');
            //             });
            //         }
            //     }
            // },
            
            
            showPublishInfo: function(signature) {
                this.showPublishInfoModal = true;
            },
            
            resumeSubscription: function () {
                var self = this;
                self.showResumeModal = false;
                axios.post('/billing/resume')
                .then(function(response) {
                    if (response.data.status == 'success') {
                        location.reload();
                    } else if (response.data.status == 'error') {
                        alert(response.data.message);
                    } else {
                        alert("Sorry, an unknown issue occured");
                    }
                }).catch(function(data) {
                    console.log("error");
                });
            }
        }
    }
</script>
